import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Position,
  useNodeId,
  useStore,
  getConnectedEdges,
  type Edge,
  Handle,
} from "@xyflow/react";
import { MyContext } from "./MyContextProvider";
import { useTranslation } from "react-i18next";
import useOutsideClick from "../../hooks/useOutsideClick";
import LoggerContext from "../../context/LoggerProvider";
import { stripTranslationLinks } from "../../lib/prescriptionTranslationKeys";
import {
  Table,
  Copy,
  Trash2,
  Edit3,
  Plus,
  Minus,
  GripVertical,
} from "lucide-react";
import { InfoPopover } from "./infoPopover";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "../ui/context-menu";
import { DndContext, closestCenter, DragEndEvent } from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  defaultAnimateLayoutChanges,
} from "@dnd-kit/sortable";

import { CSS } from "@dnd-kit/utilities";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

const BUTTON_COLORS = [
  { name: "Green", hex: "#22C55E" },
  { name: "Blue", hex: "#3B82F6" },
  { name: "Red", hex: "#EF4444" },
  { name: "Orange", hex: "#F97316" },
  { name: "Purple", hex: "#A855F7" },
  { name: "Teal", hex: "#14B8A6" },
  { name: "Pink", hex: "#EC4899" },
  { name: "Gray", hex: "#6B7280" },
];

// --- Types ---
const OPTION_ACTIONS = { REMOVE: 1, EDIT: 2 };

interface OptionType {
  text: Record<string, string>;
  value: string;
  handle: string;
  buttonColor?: string;
}

interface NodeDataType {
  label: Record<string, string>;
  options: OptionType[];
  type: string;
  selected?: boolean;
  custom?: any;
  dataitemmapping?: any[];
}

const selector = (s: any) => ({
  nodeInternals: s.nodeInternals,
});

function SortableOption({
  option,
  idx,
  nodeId,
  nodeData,
  onRemove,
  designDirection,
  isHandleConnectable,
  optionInlineEditIdx,
  labelOptionRef,
  tempOptionLabel,
  setTempOptionLabel,
  handleOptionKeyDown,
  setOptionLabelEditIntent,
  setTempOptionLabelIntent,
  handlePaneClickSimulation,
  t,
  prescriptionLanguage,
  setOptionInlineEditIdx,
  onColorChange,
}: any) {
  const { attributes, listeners, setNodeRef, transform, transition }: any =
    useSortable({ id: option.handle });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div
        className="text-xs bg-white/40 hover:bg-white/50 transition-colors duration-200
          min-w-[100px] rounded-sm px-2 py-1 text-gray-800 flex items-center gap-1
          shadow-sm border border-primary/20 mb-1"
        style={{
          padding:
            designDirection === "ltr" ? "4px 10px 4px 2px" : "4px 2px 4px 10px",
          position: "relative",
        }}
      >
        {/* Drag handle */}
        {nodeData.options.length > 1 && (
          <span
            {...attributes}
            {...listeners}
            className="text-gray-600 cursor-grab hover:text-gray-600 nodrag"
          >
            <GripVertical size={14} />
          </span>
        )}

        {/* Label */}
        {nodeData.type !== "prescription" && optionInlineEditIdx === idx ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              width: "100%",
            }}
          >
            <input
              ref={labelOptionRef}
              className="flex-1 px-1 border-none rounded outline-none bg-white/50"
              type="text"
              autoFocus
              value={tempOptionLabel}
              onChange={(e) => setTempOptionLabel(e.target.value)}
              onKeyDown={handleOptionKeyDown}
            />
          </div>
        ) : (
          <span
            className="flex-1 transition-colors cursor-pointer hover:text-primary"
            onClick={() => {
              if (optionInlineEditIdx !== -1) {
                setOptionLabelEditIntent(idx);
                setTempOptionLabelIntent(
                  option.text[prescriptionLanguage] || ""
                );
                handlePaneClickSimulation();
              } else {
                setTempOptionLabel(option.text[prescriptionLanguage] || "");
                setOptionInlineEditIdx(idx);
              }
            }}
            tabIndex={0}
            role="button"
            aria-label={`Edit option label ${idx}`}
          >
            {option.text[prescriptionLanguage] || t("no_title")}
          </span>
        )}

        {/* Color picker */}
        {nodeData.type !== "prescription" && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Change button color for option ${idx}`}
                className="flex-shrink-0 w-4 h-4 rounded-full border border-gray-300 hover:scale-110 transition-all nodrag cursor-pointer"
                style={option.buttonColor ? { backgroundColor: option.buttonColor } : { background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)" }}
                title={t("button_color")}
              />
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2 z-50" side="bottom" align="start">
              <div className="flex gap-1.5 flex-wrap max-w-[180px]">
                <button
                  type="button"
                  className={`w-6 h-6 rounded-full border-2 border-dashed transition-all ${
                    !option.buttonColor ? "border-gray-500 ring-1 ring-offset-1 ring-gray-400 scale-110" : "border-gray-300 hover:scale-105"
                  }`}
                  onClick={() => onColorChange(idx, undefined)}
                  title={t("default") || "Default"}
                />
                {BUTTON_COLORS.map((color) => (
                  <button
                    type="button"
                    key={color.hex}
                    className={`w-6 h-6 rounded-full transition-all ${
                      option.buttonColor === color.hex ? "ring-1 ring-offset-1 ring-gray-400 scale-110" : "hover:scale-105"
                    }`}
                    style={{ backgroundColor: color.hex }}
                    onClick={() => onColorChange(idx, color.hex)}
                    title={color.name}
                  />
                ))}
                <div className="relative">
                  <button
                    type="button"
                    className="w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)" }}
                  >
                    <Plus size={10} className="text-white drop-shadow-md" />
                  </button>
                  <input
                    type="color"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    value={option.buttonColor || "#3B82F6"}
                    onChange={(e) => onColorChange(idx, e.target.value)}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}

        {/* Remove button */}
        {nodeData.options.length > 1 && (
          <button
            aria-label={`Remove option ${idx}`}
            className="flex items-center justify-center w-5 h-5 p-0 text-red-600 transition-all duration-200 rounded-full hover:scale-110 hover:bg-red-100 hover:text-red-700 nodrag"
            onClick={() => onRemove(idx)}
          >
            <Minus size={12} />
          </button>
        )}

        {/* React Flow Handle - Exit Point (Diamond/Square rotated 45deg) */}
        <Handle
          type="source"
          position={designDirection === "ltr" ? Position.Right : Position.Left}
          id={`${nodeId}-${idx}`}
          className="!w-3 !h-3 !border-2 !border-white shadow-lg transition-all duration-200 hover:!w-4 hover:!h-4 hover:shadow-xl relative after:content-[''] after:absolute after:-inset-[8px] after:cursor-crosshair after:pointer-events-auto"
          style={{
            left: designDirection === "ltr" ? "inherit" : "-12px",
            right: designDirection === "rtl" ? "inherit" : "-8px",
            backgroundColor: option.value !== "?????" ? "#3b82f6" : "#ef4444",
            pointerEvents: "auto",
            zIndex: 10,
          }}
          isConnectable={
            option.value !== "?????" && isHandleConnectable(`${nodeId}-${idx}`)
          }
          title={option.value !== "?????" ? "Connect to next activity" : "Not connected"}
        />
      </div>
    </div>
  );
}

function Activity({ selected = false }: { selected?: boolean }) {
  const { t } = useTranslation();
  const { Logger } = useContext(LoggerContext);

  const elementRef = useRef<HTMLDivElement>(null);
  const labelInputRef = useRef<HTMLTextAreaElement>(null);
  const labelOptionRef = useRef<HTMLInputElement>(null);
  const currentLabelValueRef = useRef<string>(""); // ← NEW: Track current value

  // Extract context values
  const {
    data,
    setData,
    setEdges,
    edges,
    nodes,
    setNodes,
    setShowModal,
    setShowDataItemMapping,
    setSelectableContext,
    setDataItemMapping,
    setSelectedMapping,
    setMappingNodeId,
    setSelectedOption,
    selectedOption,
    setSelectedActivity,
    setShowOptionEdit,
    labelBeingEdited,
    setLabelBeingEdited,
    toBeSaved,
    setToBeSaved,
    designDirection,
    prescriptionLanguage,
    setShowActivityEdit,
    flowContext,
    generateFlowcontextFromData,
    paneWasClicked,
    setPaneWasClicked,
    setShowRemoveActivity,
    setPendingNode,
  } = useContext(MyContext);

  const nodeId = useNodeId();
  const { nodeInternals } = useStore(selector);

  // --- Local state ---
  const [optionInlineEditIdx, setOptionInlineEditIdx] = useState<number>(-1);
  const [tempOptionLabel, setTempOptionLabel] = useState<string>("");
  const [tempOptionLabelIntent, setTempOptionLabelIntent] =
    useState<string>("");
  const [labelText, setLabelText] = useState<string>("");
  const [optionLabelEditIntent, setOptionLabelEditIntent] =
    useState<number>(-1);
  const [isHovered, setIsHovered] = useState(false);

  // --- Helpers ---
  const getNodeData = useCallback(
    (): NodeDataType => data[nodeId!],
    [data, nodeId]
  );

  const nodeData: any = useMemo(() => getNodeData(), [data, nodeId]);

  // --- Option Management ---
  const onOptionRemove = useCallback(
    (idx: number) => {
      const handleId = `${nodeId}-${idx}`;
      const hasEdge = edges.some(
        (edge: Edge) => edge.sourceHandle === handleId
      );

      if (!hasEdge) {
        setSelectedOption({});
        setData((prevData: any) => {
          const cloned = { ...prevData };
          cloned[nodeId!].options = cloned[nodeId!].options.filter(
            (_: any, i: number) => i !== idx
          );
          return cloned;
        });
      } else {
        setSelectedOption({ nodeId, idx, action: OPTION_ACTIONS.REMOVE });
      }
    },
    [edges, nodeId, setSelectedOption, setData]
  );

  const onOptionAdd = useCallback(() => {
    if (optionInlineEditIdx !== -1) handlePaneClickSimulation();
    setData((prevData: any) => {
      const cloned = { ...prevData };
      cloned[nodeId!].options = [
        ...cloned[nodeId!].options,
        {
          text: { en: "", de: "", he: "" },
          value: `option${Math.floor(Math.random() * 10000)}`,
          handle: `${nodeId}-${cloned[nodeId!].options.length}`,
        },
      ];
      return cloned;
    });
  }, [optionInlineEditIdx, nodeId, setData]);

  // --- Label Editing ---
  const handleLabelChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setLabelText(value);
    currentLabelValueRef.current = value; // ← Keep in sync
  };

  const handleSave = useCallback(() => {
    if (labelBeingEdited !== nodeId) return;

    Logger.debug("HANDLESAVE: LABEL of activity should be saved", nodeId);

    const valueToSave =
      labelInputRef.current?.value ?? currentLabelValueRef.current;

    setLabelBeingEdited(null);
    setData((prevData: any) => {
      const cloned = { ...prevData };
      cloned[nodeId!].label = {
        ...cloned[nodeId!].label,
        [prescriptionLanguage]: valueToSave,
      };
      return cloned;
    });

    currentLabelValueRef.current = ""; // Reset
  }, [
    Logger,
    nodeId,
    prescriptionLanguage,
    setLabelBeingEdited,
    setData,
    labelBeingEdited,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") {
      setLabelBeingEdited(null);
      setLabelText(nodeData.label[prescriptionLanguage] || "");
      currentLabelValueRef.current = "";
    }
    e.stopPropagation();
  };

  // --- Option Label Editing ---
  const saveOptionLabel = useCallback(() => {
    setData((prevData: any) => {
      const newData = { ...prevData };
      const options = [...newData[nodeId!].options];
      options[optionInlineEditIdx] = {
        ...options[optionInlineEditIdx],
        text: {
          ...options[optionInlineEditIdx].text,
          [prescriptionLanguage]: tempOptionLabel,
        },
      };
      newData[nodeId!].options = options;
      return newData;
    });

    if (optionLabelEditIntent !== -1) {
      setTempOptionLabel(tempOptionLabelIntent);
      setOptionInlineEditIdx(optionLabelEditIntent);
      setOptionLabelEditIntent(-1);
      setTempOptionLabelIntent("");
    } else {
      setOptionInlineEditIdx(-1);
    }
  }, [
    nodeId,
    optionInlineEditIdx,
    tempOptionLabel,
    prescriptionLanguage,
    optionLabelEditIntent,
    tempOptionLabelIntent,
    setData,
  ]);

  const handleOptionKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") saveOptionLabel();
    if (e.key === "Escape") setOptionInlineEditIdx(-1);
    e.stopPropagation();
  };

  const handleOptionColorChange = useCallback((optIdx: number, color: string | undefined) => {
    setData((prevData: any) => {
      const newData = { ...prevData };
      const options = [...newData[nodeId!].options];
      options[optIdx] = { ...options[optIdx], buttonColor: color };
      newData[nodeId!].options = options;
      return newData;
    });
  }, [nodeId, setData]);

  // --- React Flow handle connectability ---
  const isHandleConnectable = useCallback(
    (handleId: string) => {
      const node = nodeInternals?.get(nodeId);
      if (!node) return true;
      const connectedEdges = getConnectedEdges([node], edges);
      return !connectedEdges.some((edge) => edge.sourceHandle === handleId);
    },
    [nodeInternals, nodeId, edges]
  );

  // --- Activity Type ---
  const changeActivityType = useCallback(() => {
    if (nodeData.type === "default") {
      setData((prevData: any) => {
        const clonedData = { ...prevData };
        Object.entries(clonedData).forEach(([key, nodeData]: [string, any]) => {
          if (nodeData.type !== "prescription") nodeData.type = "default";
        });
        clonedData[nodeId!].type = "start";
        return clonedData;
      });
    }
  }, [nodeId, setData, getNodeData]);

  // --- Context Menu Actions ---
  const handleStartDuplicate = useCallback(() => {
    const nodeToDuplicate = nodes.find((n: any) => n.id === nodeId);
    if (!nodeToDuplicate) return;

    const newNodeId = Math.floor(10000 + Math.random() * 90000).toString();

    setData((prevData: any) => {
      const newNodeData = {
        ...prevData[nodeId!],
        type: "default",
        options: prevData[nodeId!].options.map((option: any, index: any) => ({
          ...option,
          handle: `${newNodeId}-${index}`,
          value: `option${Math.floor(1000 + Math.random() * 9000)}`,
        })),
        // Duplicated content is new content — it must not keep pointing at
        // the original block's translation-table rows. See
        // stripTranslationLinks above.
        custom: Array.isArray(prevData[nodeId!].custom)
          ? stripTranslationLinks(prevData[nodeId!].custom)
          : prevData[nodeId!].custom,
      };

      const duplicatedNode = {
        ...nodeToDuplicate,
        id: newNodeId,
        type: "activity",
        selected: true,
        data: { ...nodeToDuplicate.data, id: newNodeId, ...newNodeData },
      };
      setPendingNode(duplicatedNode);
      return {
        ...prevData,
        [newNodeId]: newNodeData,
      };
    });
  }, [nodes, setData]);

  const handleDelete = useCallback(() => {
    Logger.debug("Delete node", nodeId);
    setShowRemoveActivity((prev: any) => !prev);
  }, [nodeId, Logger]);

  // --- UI helpers ---
  const getBackgroundColorForType = (type: string) => {
    switch (type) {
      case "start":
        return "#10b981";
      case "default":
        return "#3b82f6";
      case "prescription":
        return "#f59e0b";
      default:
        return "#6b7280";
    }
  };

  // --- Simulate pane click ---
  const handlePaneClickSimulation = useCallback(() => {
    setPaneWasClicked(true);
  }, [setPaneWasClicked]);

  // --- Outside click handlers ---
  useOutsideClick(labelInputRef, () => {
    if (labelBeingEdited === nodeId) {
      handleSave();
    }
  });

  useOutsideClick(labelOptionRef, handlePaneClickSimulation);

  // --- Effects ---
  useEffect(() => {
    if (selectedOption.nodeId) {
      if (selectedOption.action === OPTION_ACTIONS.REMOVE) setShowModal(true);
      if (selectedOption.action === OPTION_ACTIONS.EDIT)
        setShowOptionEdit(true);
    }
  }, [selectedOption, setShowModal, setShowOptionEdit]);

  useEffect(() => {
    if (paneWasClicked && optionInlineEditIdx !== -1) {
      saveOptionLabel();
      setPaneWasClicked(false);
    }
  }, [paneWasClicked, optionInlineEditIdx, saveOptionLabel, setPaneWasClicked]);

  useEffect(() => {
    setLabelText(nodeData.label[prescriptionLanguage] || "");
  }, [data, nodeId, prescriptionLanguage, getNodeData]);

  useEffect(() => {
    if (toBeSaved && labelBeingEdited === nodeId) {
      handleSave();
      setToBeSaved(false);
    }
  }, [toBeSaved, labelBeingEdited, nodeId, handleSave, setToBeSaved]);

  // ← NEW: Save if edit mode leaves this node with unsaved changes
  useEffect(() => {
    if (
      labelBeingEdited !== nodeId &&
      labelBeingEdited !== null &&
      currentLabelValueRef.current !== "" &&
      currentLabelValueRef.current !== nodeData.label[prescriptionLanguage]
    ) {
      setData((prevData: any) => {
        const cloned = { ...prevData };
        cloned[nodeId!].label = {
          ...cloned[nodeId!].label,
          [prescriptionLanguage]: currentLabelValueRef.current,
        };
        return cloned;
      });
      currentLabelValueRef.current = "";
    }
  }, [labelBeingEdited, nodeId, prescriptionLanguage, nodeData.label, setData]);

  // --- Option Render ---
  const renderOption = (option: OptionType, idx: number) => (
    <SortableOption
      key={option.handle}
      idx={idx}
      option={option}
      nodeId={nodeId}
      nodeData={nodeData}
      onRemove={onOptionRemove}
      designDirection={designDirection}
      isHandleConnectable={isHandleConnectable}
      optionInlineEditIdx={optionInlineEditIdx}
      setOptionInlineEditIdx={setOptionInlineEditIdx}
      tempOptionLabel={tempOptionLabel}
      setTempOptionLabel={setTempOptionLabel}
      handleOptionKeyDown={handleOptionKeyDown}
      labelOptionRef={labelOptionRef}
      setSelectedOption={setSelectedOption}
      setOptionLabelEditIntent={setOptionLabelEditIntent}
      setTempOptionLabelIntent={setTempOptionLabelIntent}
      handlePaneClickSimulation={handlePaneClickSimulation}
      t={t}
      prescriptionLanguage={prescriptionLanguage}
      onColorChange={handleOptionColorChange}
    />
  );

  const bgColor = getBackgroundColorForType(nodeData?.type);

  return (
    <div className="relative">
      {/* Target Handles */}
      <Handle
        type="target"
        id={`${nodeId}-targetSideLeft`}
        position={designDirection === "ltr" ? Position.Left : Position.Right}
        className="!w-4 !h-4 !border-2 !border-white shadow-lg transition-all duration-200
          hover:!w-5 hover:!h-5 hover:shadow-xl"
        style={{
          backgroundColor: bgColor,
          right: designDirection === "ltr" ? "inherit" : "-8px",
          left: designDirection === "rtl" ? "inherit" : "-8px",
        }}
        title="Connect from another activity"
      />

      <Handle
        type="target"
        id={`${nodeId}-targetSideTop`}
        position={Position.Top}
        className="!w-4 !h-4 !border-2 !border-white shadow-lg transition-all duration-200
          hover:!w-5 hover:!h-5 hover:shadow-xl"
        style={{
          top: "-8px",
          backgroundColor: bgColor,
        }}
        title="Connect from another activity"
      />

      <Handle
        type="target"
        id={`${nodeId}-targetSideBottom`}
        position={Position.Bottom}
        className="!w-4 !h-4 !border-2 !border-white shadow-lg transition-all duration-200
          hover:!w-5 hover:!h-5 hover:shadow-xl"
        style={{
          bottom: "-8px",
          backgroundColor: bgColor,
        }}
        title="Connect from another activity"
      />

      <ContextMenu modal={false}>
        <ContextMenuTrigger>
          {/* Main Node Container */}
          <div
            className={`relative transition-all duration-200 w-[200px] ${
              selected
                ? "ring-2 ring-orange-400 transform scale-105"
                : nodeData?.selected
                ? "ring-2 ring-orange-400 ring-offset-2"
                : ""
            } ${isHovered ? "transform scale-101 shadow-xl" : "shadow-lg"}`}
            style={{ borderRadius: "12px" }}
            ref={elementRef}
            dir={designDirection}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div
              className="relative text-white rounded-lg"
              style={{
                background: `linear-gradient(135deg, ${bgColor} 0%, ${bgColor}dd 100%)`,
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Header with label */}
              <div
                className="text-center px-2 py-1 border-b mb-2 w-full text-[14px] bg-black/15 relative cursor-pointer
                hover:bg-black/5 transition-all duration-200"
                onClick={() =>
                  nodeData.type !== "prescription" &&
                  setLabelBeingEdited(nodeId)
                }
              >
                {labelBeingEdited === nodeId ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <textarea
                      ref={labelInputRef}
                      value={labelText}
                      onChange={handleLabelChange}
                      onKeyDown={handleKeyDown}
                      onBlur={handleSave} // ← CRITICAL: Save on blur
                      className="w-full text-center text-white bg-transparent border-none outline-none placeholder-white/70 max-w-[190px] h-auto resize-none line-break break-words"
                      autoFocus
                    />
                  </div>
                ) : (
                  <div className="flex items-center justify-center w-full gap-2">
                    <span>
                      {nodeData?.label[prescriptionLanguage] || t("no_title")}
                    </span>
                  </div>
                )}
              </div>

              {/* Options */}
              <div className="px-2 pb-2 space-y-2">
                <DndContext
                  collisionDetection={closestCenter}
                  onDragEnd={(event: DragEndEvent) => {
                    const { active, over } = event;
                    if (!over || !nodeId) return;

                    setData((prev: any) => {
                      const oldIndex = prev[nodeId].options.findIndex(
                        (o: any) => o.handle === active.id
                      );
                      const newIndex = prev[nodeId].options.findIndex(
                        (o: any) => o.handle === over.id
                      );

                      if (
                        oldIndex === -1 ||
                        newIndex === -1 ||
                        oldIndex === newIndex
                      )
                        return prev;

                      const newOptions = arrayMove(
                        [...prev[nodeId].options],
                        oldIndex,
                        newIndex
                      );

                      const handleMap: Record<string, string> = {};
                      newOptions.forEach((option, idx) => {
                        handleMap[option.handle] = `${nodeId}-${idx}`;
                        option.handle = `${nodeId}-${idx}`;
                      });

                      setEdges((prevEdges: any) => {
                        const updatedEdges = prevEdges.map((edge: any) => {
                          if (
                            edge.source === nodeId &&
                            handleMap[edge.sourceHandle]
                          ) {
                            return {
                              ...edge,
                              sourceHandle: handleMap[edge.sourceHandle],
                            };
                          }
                          return edge;
                        });
                        return updatedEdges;
                      });

                      return {
                        ...prev,
                        [nodeId]: {
                          ...prev[nodeId],
                          options: newOptions,
                        },
                      };
                    });
                  }}
                >
                  <SortableContext
                    items={nodeData.options.map((o: any) => o.handle)}
                    strategy={verticalListSortingStrategy}
                  >
                    {nodeData.options.map((option: OptionType, idx: number) =>
                      renderOption(option, idx)
                    )}
                  </SortableContext>
                </DndContext>
              </div>

              {/* Add Option Button */}
              {nodeData.type !== "prescription" ? (
                <div className="px-2 pb-3">
                  <button
                    aria-label="Add option"
                    className="flex items-center justify-center w-full py-1 text-xs font-medium transition-all duration-200 border-2 border-dashed rounded-sm border-white/40 hover:border-white/60 hover:bg-white/10"
                    onClick={onOptionAdd}
                  >
                    <Plus size={10} />
                    {t("add_option")}
                  </button>
                </div>
              ) : (
                <div className="pb-2"></div>
              )}

              {/* Data Mapping Icon */}
              {nodeData.type === "prescription" &&
                (nodeData?.dataitemmapping?.length || 0) > 0 && (
                  <div className="absolute top-2 left-2">
                    <Table
                      onClick={async () => {
                        setDataItemMapping(nodeData.dataitemmapping);
                        const selectableContext: any[] = [];
                        const referenceFlowContext =
                          structuredClone(flowContext);
                        await generateFlowcontextFromData(
                          data,
                          selectableContext,
                          "",
                          nodeId,
                          referenceFlowContext
                        );
                        const alreadySelected = nodeData?.dataitemmapping?.map(
                          (item: any) => item.mappedToOuter.key
                        );
                        setSelectedMapping(alreadySelected);
                        setSelectableContext(selectableContext);
                        setShowDataItemMapping(true);
                        setMappingNodeId(nodeId);
                      }}
                      className="w-5 h-5 transition-transform cursor-pointer hover:scale-110 text-white/80 hover:text-white"
                    />
                  </div>
                )}

              {/* More Options Icon */}
              {isHovered && nodeData.type !== "prescription" && (
                <div className="absolute top-2 right-1.5">
                  <InfoPopover data={data} setData={setData} nodeId={nodeId} />
                </div>
              )}
            </div>
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className="w-52">
          <ContextMenuItem onClick={handleStartDuplicate}>
            <Copy className="w-4 h-4 mr-2" />
            <span>{t("duplicate")}</span>
          </ContextMenuItem>
          <ContextMenuItem className="text-red-600" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-2" /> <span>{t("delete")}</span>
          </ContextMenuItem>
        </ContextMenuContent>
      </ContextMenu>

      {/* Edit Icon (shown when selected) */}
      {selected && nodeData.type !== "prescription" && (
        <div className="absolute -top-5 right-2">
          <button
            className="flex items-center justify-center w-6 h-6 text-orange-400 transition-all duration-200 bg-white rounded-full shadow-lg ring-2 ring-orange-400 hover:shadow-xl hover:scale-110"
            onClick={() => {
              setShowActivityEdit(true);
              setSelectedActivity(nodeId);
            }}
          >
            <Edit3 size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default Activity;
